import { Row } from '@tanstack/react-table';
import { memo, useMemo } from 'react';
import { FormProvider } from 'react-hook-form';
import { Content } from 'src/contexts/ContentContext';
import { AlertWithSuppressionDialog } from 'src/dialogs/AlertWithSuppressionDialog';
import { ContentId } from 'src/utils/constants/contentId';

import { ListingWithEvent } from '../ListingTable.types';
import { useListingPriceForm } from './ListingPriceForm.hooks';

const ListingPricingFormRaw = ({
  rowData,
  tableRow,
}: {
  rowData: ListingWithEvent | null;
  tableRow: React.ReactNode;
}) => {
  const {
    alerts,
    warningDialog,
    viagogoVirtualId,
    methods,
    onCancelSuspressionDialog,
    submitPriceChange,
  } = useListingPriceForm(rowData);

  return (
    <FormProvider key={rowData?.listing?.id} {...methods}>
      {tableRow}
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        onOkay={() => {
          submitPriceChange();
          warningDialog.closeDialog();
        }}
        onCancel={onCancelSuspressionDialog}
        viagogoVirtualId={viagogoVirtualId}
      />
    </FormProvider>
  );
};

export const ListingPricingForm = memo(ListingPricingFormRaw);

type ListingPricingFormRawV2 = {
  /**
   * Row columns joint with ','
   *
   * Important!!!
   * React.memo track the props, only rerender when the props changes.
   * Dummy prop to force re-render the table row when columns/order changes.
   */
  columns: string;
  row: Row<ListingWithEvent>;
  rowIndex: number;
  getRowContent: (row: Row<ListingWithEvent>) => React.ReactNode;
};

const ListingPricingFormRawV2 = ({
  row,
  rowIndex,
  getRowContent,
  columns,
}: ListingPricingFormRawV2) => {
  const rowData = useMemo(() => {
    if (row.original) {
      return {
        ...row.original,
        rowIndex: rowIndex,
      } as ListingWithEvent;
    }
    return null;
  }, [row.original, rowIndex]);

  const {
    alerts,
    warningDialog,
    viagogoVirtualId,
    methods,
    onCancelSuspressionDialog,
    submitPriceChange,
  } = useListingPriceForm(rowData);

  return (
    <FormProvider key={rowData?.listing?.id} {...methods}>
      {getRowContent(row)}
      <AlertWithSuppressionDialog
        headerText={<Content id={ContentId.ManualPriceWarning} />}
        size="md"
        {...warningDialog.dialogProps}
        alerts={alerts}
        onOkay={() => {
          submitPriceChange();
          warningDialog.closeDialog();
        }}
        onCancel={onCancelSuspressionDialog}
        viagogoVirtualId={viagogoVirtualId}
      />
    </FormProvider>
  );
};

export const ListingPricingFormV2 = memo(ListingPricingFormRawV2);
